// src/utils/apiUtils.js - ENHANCED WITH RETRY LOGIC
import { API_BASE_URL } from '../config/api';

// ✅ NEW: Retry configuration
const RETRY_CONFIG = {
    maxRetries: 3,
    retryDelay: 1000, // 1 second
    retryableStatusCodes: [503, 502, 504, 500], // Service unavailable, bad gateway, gateway timeout, server error
    retryableCodes: ['DB_NOT_AVAILABLE', 'DB_NOT_CONNECTED', 'DB_CONNECTION_LOST', 'DB_ERROR']
};

// ✅ NEW: Sleep function for delays
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ✅ NEW: Check if error is retryable
const isRetryableError = (error) => {
    if (!error) return false;

    // Check status code
    if (error.status && RETRY_CONFIG.retryableStatusCodes.includes(error.status)) {
        return true;
    }

    // Check error code
    if (error.code && RETRY_CONFIG.retryableCodes.includes(error.code)) {
        return true;
    }

    // Check error message
    const message = error.message?.toLowerCase() || '';
    return message.includes('service temporarily unavailable') ||
        message.includes('database connection') ||
        message.includes('connection unavailable');
};

// ✅ ENHANCED: API call with retry logic
const apiCall = async (endpoint, options = {}, toast, retryCount = 0) => {
    // Normalize endpoint to remove base URL if present
    const normalizedEndpoint = endpoint.startsWith(API_BASE_URL)
        ? endpoint.slice(API_BASE_URL.length)
        : endpoint;

    // Ensure the endpoint starts with a slash
    const cleanEndpoint = normalizedEndpoint.startsWith('/')
        ? normalizedEndpoint
        : `/${normalizedEndpoint}`;

    const url = `${API_BASE_URL}${cleanEndpoint}`;

    try {
        const response = await fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Content-Type': 'application/json',
                ...(localStorage.getItem('accessToken') && {
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
                }),
                ...(localStorage.getItem('companySlug') && {
                    'x-org-slug': localStorage.getItem('companySlug')
                }),
            },
        });

        const data = await response.json();

        if (!response.ok) {
            const errorMessage = data.msg || data.message || (typeof data === 'string' ? data : JSON.stringify(data)) || 'API call failed';
            const error = new Error(errorMessage);
            error.status = response.status;
            error.code = data.code;
            // Carry billing/validation extra fields so callers can read them
            if (data.limit !== undefined) error.limit = data.limit;
            if (data.currentCount !== undefined) error.currentCount = data.currentCount;
            if (data.available !== undefined) error.available = data.available;
            throw error;
        }

        return data;
    } catch (error) {
        console.error(`[apiCall] Error on attempt ${retryCount + 1}:`, error.message);

        // ✅ Check if we should retry
        const shouldRetry = isRetryableError(error) && retryCount < RETRY_CONFIG.maxRetries;

        if (shouldRetry) {
            const delay = RETRY_CONFIG.retryDelay * Math.pow(2, retryCount); // Exponential backoff
            // console.log(`[apiCall] Retrying in ${delay}ms... (attempt ${retryCount + 1}/${RETRY_CONFIG.maxRetries})`);

            // Show a subtle notification for the first retry only
            if (retryCount === 0 && toast) {
                toast({
                    title: 'Connection Issue',
                    description: 'Retrying request...',
                    status: 'warning',
                    duration: 2000,
                    isClosable: true,
                });
            }

            await sleep(delay);
            return apiCall(endpoint, options, toast, retryCount + 1);
        }

        // ✅ Don't show toast for certain error codes or if we're out of retries
        const suppressToast = error.code === 'DB_NOT_CONNECTED' ||
            error.code === 'DB_CONNECTION_LOST' ||
            error.code === 'TOKEN_EXPIRED' || // Let auth context handle this
            (error.status === 503 && retryCount > 0); // Already showed retry message

        if (toast && !suppressToast) {
            // Customize message based on error type
            let description = error.message;

            if (error.status === 503 || isRetryableError(error)) {
                description = 'Service temporarily unavailable. Please try again in a moment.';
            }

            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }

        throw error;
    }
};

// ✅ NEW: Health check function
const checkHealth = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/health`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const data = await response.json();
        return {
            healthy: response.ok,
            data
        };
    } catch (error) {
        console.error('[checkHealth] Health check failed:', error);
        return {
            healthy: false,
            error: error.message
        };
    }
};

export { apiCall, checkHealth };